import type { CallPhase } from './callMachine'

/**
 * QUE FAIRE D'UNE QUALIFICATION QUI ARRIVE — déposer, et libérer ou non.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * LE DÉFAUT QUE CETTE RÈGLE FERME (mineur b de la review de #47)
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Entre le clic de l'agent et l'exécution du gestionnaire, un ENTRANT peut
 * avoir été présenté. La machine porte alors un AUTRE `callId`, et deux
 * réactions étaient possibles — les deux mauvaises :
 *
 *  - **relire l'instantané** et écrire dessus ⇒ le résultat part sur l'appel
 *    ENTRANT. On qualifie « Pas de réponse » l'appel de quelqu'un qui est en
 *    train de nous parler ;
 *  - **tout refuser** (ce que faisait la première version de SCENE-2B) ⇒ la
 *    qualification que l'agent venait de saisir est **perdue**, alors que
 *    master, lui, l'enregistrait. C'est une régression.
 *
 * La règle : **on DÉPOSE toujours** — l'écriture n'a besoin que de l'identité
 * de l'appel, et la scène l'a figée à l'entrée en wrapup —, et **seule la
 * libération de la machine est conditionnelle**. Un `reset()` inconditionnel
 * avalerait l'entrant qui vient d'être présenté : écran vide, appelant qui
 * sonne dans le vide jusqu'au manqué (garde M3, conservée).
 *
 * Fonction PURE : c'est elle que le spec exerce, sans monter le provider.
 */

/**
 * IDENTITÉ FIGÉE de l'appel qualifié — photographiée par la scène à l'ENTRÉE en
 * wrapup, jamais relue au moment du clic.
 *
 * C'est le seul instant où elle est certaine : ensuite, un entrant peut
 * s'intercaler et remplacer `callId`/`campaignContactId` dans la machine.
 */
export interface QualifiedCall {
  callId: string
  campaignContactId: string | null
  label: string
}

export interface OutcomeSubmissionInput {
  /** Phase de la machine AU MOMENT du dépôt. */
  phase: CallPhase
  /** `callId` porté par la machine à cet instant — peut être celui d'un entrant. */
  currentCallId: string | null
  /** Identité FIGÉE de l'appel à qualifier (photo prise à l'entrée en wrapup). */
  targetCallId: string | null
}

export interface OutcomeSubmissionPlan {
  /** Déposer l'écriture dans la file. Faux uniquement s'il n'y a rien à écrire. */
  enqueue: boolean
  /** Remettre la machine au repos (donc faire avancer l'enchaînement). */
  releaseMachine: boolean
}

export function planOutcomeSubmission(
  input: OutcomeSubmissionInput,
): OutcomeSubmissionPlan {
  const { phase, currentCallId, targetCallId } = input

  // Aucun `Call` à qualifier (échec avant sa création) : il n'y a rien à
  // écrire, mais l'agent ne doit pas rester coincé dans un formulaire vide.
  if (!targetCallId) {
    return { enqueue: false, releaseMachine: phase === 'wrapup' }
  }

  return {
    // TOUJOURS. La qualification est saisie, elle part.
    enqueue: true,
    // …mais on ne libère que si la machine désigne encore CET appel.
    releaseMachine: phase === 'wrapup' && currentCallId === targetCallId,
  }
}
